/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ActionButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import React, { useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { defineMessages } from 'react-intl';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';

export type CopyButtonProps = IButtonProps & {
    /**
     * The text to copy when button is clicked.
     */
    textToCopy: string;

    /**
     * What is being copied
     */
    label: string;

    /**
     * Indicates whether the button is disabled or not.
     */
    disabled?: boolean;
};

const messages = defineMessages({
    initialState: {
        id: 'core.copyButton.initialState',
        defaultMessage: 'Click to copy'
    },
    clickedState: {
        id: 'core.copyButton.clickedState',
        defaultMessage: 'Copied'
    }
});

const copyButtonTheme = getScopedTheme('CopyButton');
const ContainerButton = (props: IButtonProps) => <ActionButton {...props} styles={mergeStyleSets(containerButtonStyles, props.styles)} />;
const containerButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ root: { color: copyButtonTheme.iconColor } });

/**
 * Renders a copy button component that is used to copy the
 * given display text to the clipboard when that button is clicked.
 * Used in utilities like copying user id, app id, auth key, etc.
 *
 * @param props The copy button input.
 */
export const CopyButton = (props: CopyButtonProps) => {
    const { textToCopy, label, disabled, ...rest } = props;
    const { localize } = useLocalization();
    const [displayedMessage, setDisplayedMessage] = useState(messages.initialState);

    const onMouseClick = () => setDisplayedMessage(messages.clickedState);

    const onMouseLeave = () => setDisplayedMessage(messages.initialState);

    return (
        <Stack aria-live="polite">
            <LuisTooltip content={localize(displayedMessage)} calloutProps={{ calloutMaxWidth: 100, gapSpace: 0 }} hidden={disabled}>
                <CopyToClipboard onCopy={onMouseClick} text={textToCopy}>
                    <ContainerButton
                        {...rest}
                        disabled={disabled}
                        onMouseLeave={onMouseLeave}
                        iconProps={{ iconName: 'Copy' }}
                        aria-label={`${localize(displayedMessage)} ${label}`}
                        ariaDescription={localize(displayedMessage)}
                    />
                </CopyToClipboard>
            </LuisTooltip>
        </Stack>
    );
};
